import type {
  ProcurementOrdersResponse,
  ProcurementRequest,
  ProcurementSummaryResponse,
} from '@contracts/procurement';
import { resolvePeriod } from '@/lib/period';
import { authedFetch } from '@/lib/apiClient';
import { BRIDGE_URL as BRIDGE } from '@/lib/config';

/**
 * Única puerta de datos del dominio Compras/Gastos. Igual que el resto de
 * `services/`, el front solo conoce a BridgeHub — nunca a Odoo. La lógica de
 * negocio (SLA, vencida, días hábiles) llega YA resuelta del BFF.
 */
function scopeParams(req: ProcurementRequest): URLSearchParams {
  const params = new URLSearchParams();
  // Sin periodo = todo el histórico (el BFF omite el filtro de fechas).
  if (req.period) {
    const { from, to } = resolvePeriod(req.period);
    params.set('periodFrom', from);
    params.set('periodTo', to);
  }
  if (req.companies?.length) params.set('companies', req.companies.join(','));
  if (req.scope?.states?.length) params.set('states', req.scope.states.join(','));
  if (req.scope?.departments?.length) params.set('departments', req.scope.departments.join(','));
  if (req.scope?.urgency) params.set('urgency', req.scope.urgency);
  if (req.scope?.slaState) params.set('slaState', req.scope.slaState);
  if (req.scope?.requesterId != null) params.set('requesterId', String(req.scope.requesterId));
  return params;
}

/** KPIs del tablero — `GET /procurement/summary`. */
export async function fetchProcurementSummary(
  req: ProcurementRequest,
): Promise<ProcurementSummaryResponse> {
  const res = await authedFetch(`${BRIDGE}/procurement/summary?${scopeParams(req).toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener el resumen de compras');
  return body.data as ProcurementSummaryResponse;
}

/** Lista de órdenes de compra — `GET /procurement/orders`. */
export async function fetchProcurementOrders(
  req: ProcurementRequest,
): Promise<ProcurementOrdersResponse> {
  const res = await authedFetch(`${BRIDGE}/procurement/orders?${scopeParams(req).toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener las órdenes de compra');
  return body.data as ProcurementOrdersResponse;
}
